import React, { useState, useEffect, useMemo } from 'react';
import { Save, Play, RefreshCw, AlertTriangle, Check, History, LayoutTemplate, Plus, Edit2, RotateCcw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

/**
 * Advanced AI Settings Editor
 * Allows managing prompt tasks, editing system instructions, and viewing version history.
 */
const PromptManager = () => {
    // --- State ---
    const [tasks, setTasks] = useState([]); // from /api/admin/ai/tasks
    const [prompts, setPrompts] = useState([]); // from /api/admin/ai/prompts
    const [configs, setConfigs] = useState([]); // from /api/admin/ai/models (Active Configuration)

    // Selection State
    const [selectedTask, setSelectedTask] = useState(null); // task_type string
    // const [selectedModel, setSelectedModel] = useState('default'); // Removed in cleanup phase
    const selectedModel = 'default'; // Always edit default/base prompt in this view now

    // UI State
    const [isCreatingTask, setIsCreatingTask] = useState(false);
    const [newTaskName, setNewTaskName] = useState('');

    // --- Data Fetching ---
    const fetchAll = React.useCallback(async () => {
        try {
            const [tRes, pRes, cRes] = await Promise.all([
                fetch('/api/admin/ai/tasks'),
                fetch('/api/admin/ai/prompts'),
                fetch('/api/admin/ai/models')
            ]);
            if (tRes.ok) setTasks(await tRes.json());
            if (pRes.ok) setPrompts(await pRes.json());
            if (cRes.ok) setConfigs(await cRes.json());
        } catch (err) {
            console.error("Fetch error", err);
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // --- Derived Data ---
    // The Active (Current) Prompt
    const activePromptRow = useMemo(() => {
        if (!selectedTask) return null;
        // Prioritize exact model match, fallback to default for display if needed
        // But for editing, we want to know exactly what we are editing.
        const exact = prompts.find(p => p.task_type === selectedTask && p.model_name === selectedModel && p.is_active);
        if (exact) return exact;

        // Fallback to default if specific model selected but no override exists
        if (selectedModel !== 'default') {
            return prompts.find(p => p.task_type === selectedTask && p.model_name === 'default' && p.is_active);
        }
        return null;
    }, [prompts, selectedTask, selectedModel]);

    // Active Model for the selected task (from System Config)
    const activeModelForTask = useMemo(() => {
        if (!selectedTask) return null;

        // Phase 1 Mapping: Map legacy prompt task names to config role names
        const ROLE_MAPPING = {
            'chat': 'default_chat',
            'product_description': 'seo_description'
        };

        const lookupRole = ROLE_MAPPING[selectedTask] || selectedTask;

        const config = configs.find(c => c.role_name === lookupRole);
        return config ? config.model_name : 'mistral-large:123b'; // Default fallback assumption
    }, [configs, selectedTask]);

    // History for the selected task/model
    const promptHistory = useMemo(() => {
        if (!selectedTask) return [];
        return prompts.filter(p =>
            p.task_type === selectedTask &&
            p.model_name === selectedModel
        ).sort((a, b) => b.version - a.version);
    }, [prompts, selectedTask, selectedModel]);

    // Effective Content (Handling fallback inheritance for display)
    const effectivePromptText = useMemo(() => {
        if (activePromptRow) return activePromptRow.prompt_text;
        // If we selected a model but it has no override, showing the default is helpful context
        const defaultPrompt = prompts.find(p => p.task_type === selectedTask && p.model_name === 'default' && p.is_active);
        return defaultPrompt ? defaultPrompt.prompt_text : "";
    }, [activePromptRow, prompts, selectedTask]);

    const effectiveVariables = useMemo(() => {
        if (activePromptRow && activePromptRow.variables) return activePromptRow.variables;
        const taskDef = tasks.find(t => t.task_type === selectedTask);
        return taskDef?.variables || [];
    }, [activePromptRow, tasks, selectedTask]);

    const effectiveDescription = useMemo(() => {
        if (activePromptRow && activePromptRow.description) return activePromptRow.description;
        const taskDef = tasks.find(t => t.task_type === selectedTask);
        return taskDef?.description || "";
    }, [activePromptRow, tasks, selectedTask]);


    // --- Handlers ---

    const handleCreateTask = async () => {
        if (!newTaskName.trim()) return;
        // Just selecting it will trigger the "New Prompt" flow in the editor if it doesn't exist
        // But we want to register it formally maybe? 
        // For now, let's just add it to the local list so we can select it, 
        // the backend registers it on first prompt save.
        if (!tasks.find(t => t.task_type === newTaskName)) {
            setTasks(prev => [...prev, { task_type: newTaskName, description: 'New Custom Task', variables: [] }]);
        }
        setSelectedTask(newTaskName);
        setIsCreatingTask(false);
        setNewTaskName('');
    };

    const handleSaveContext = async (content, metadata) => {
        if (!selectedTask) return;

        const payload = {
            model_name: selectedModel,
            task_type: selectedTask,
            prompt_text: content,
            variables: metadata.variables, // JSON array
            description: metadata.description,
            is_active: true,
            locale: 'de'
        };

        try {
            const res = await fetch('/api/admin/ai/prompts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                await fetchAll(); // Refresh to get new ID/Version
            }
        } catch (err) {
            console.error("Save failed", err);
        }
    };

    // Restore an old version (Save it as new version)
    const handleRollback = (oldPrompt) => {
        handleSaveContext(oldPrompt.prompt_text, {
            description: oldPrompt.description,
            variables: oldPrompt.variables
        });
    };

    return (
        <div className="h-full flex gap-4 text-stone-300">
            {/* LEFT: Task List */}
            <div className="w-1/4 min-w-[250px] bg-stone-900 border border-stone-800 rounded-lg flex flex-col overflow-hidden">
                <div className="p-4 border-b border-stone-800 bg-stone-950/30 flex justify-between items-center">
                    <h3 className="font-bold text-white flex items-center gap-2">
                        <LayoutTemplate size={18} className="text-blue-500" />
                        Tasks
                    </h3>
                    <button onClick={() => setIsCreatingTask(true)} className="p-1 hover:bg-stone-800 rounded text-stone-400 hover:text-white" title="New Task">
                        <Plus size={16} />
                    </button>
                </div>

                {isCreatingTask && (
                    <div className="p-2 bg-stone-800/50 border-b border-stone-800">
                        <input
                            autoFocus
                            className="w-full bg-stone-950 border border-stone-700 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-blue-500"
                            placeholder="task_name (e.g. support_agent)"
                            value={newTaskName}
                            onChange={e => setNewTaskName(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                            onKeyDown={e => e.key === 'Enter' && handleCreateTask()}
                            onBlur={() => !newTaskName && setIsCreatingTask(false)}
                        />
                    </div>
                )}

                <div className="flex-1 overflow-y-auto">
                    {tasks.map(t => (
                        <button
                            key={t.task_type}
                            onClick={() => {
                                setSelectedTask(t.task_type);
                                // Auto-select active model for this task -> DISABLED in Phase 1 Cleanup
                                // We always view 'default' prompt to keep Brain agnostic.
                                // The active model is purely for verification/display.
                                // Removed: setSelectedModel(...)
                            }}
                            className={`w-full text-left p-3 border-b border-stone-800/50 hover:bg-stone-800/50 transition-colors ${selectedTask === t.task_type ? 'bg-blue-900/10 border-l-4 border-l-blue-500' : 'border-l-4 border-l-transparent'}`}
                        >
                            <div className="font-bold text-sm text-stone-200">{t.task_type}</div>
                            <div className="text-xs text-stone-500 truncate">
                                {t.description || "No description"}
                                {/* Show active model if known */}
                                {configs.find(c => c.role_name === t.task_type) && (
                                    <span className="ml-2 text-[10px] text-blue-400 opacity-70">
                                        ({configs.find(c => c.role_name === t.task_type).model_name})
                                    </span>
                                )}
                            </div>
                        </button>
                    ))}
                    {tasks.length === 0 && <div className="p-4 text-stone-500 italic text-sm">No tasks found.</div>}
                </div>
            </div>

            {/* RIGHT: Editor & History Container */}
            <div className="flex-1 flex flex-col bg-stone-900 border border-stone-800 rounded-lg overflow-hidden">
                <div className="p-4 border-b border-stone-800 bg-stone-950/30 flex justify-between items-center h-14">
                    <div className="flex items-center gap-4">
                        {selectedTask ? (
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-white text-lg">{selectedTask}</span>
                                <span className="text-stone-600">/</span>
                                <div className="flex items-center gap-2 bg-stone-950 border border-stone-800 rounded-full px-3 py-1">
                                    <span className="text-[10px] text-stone-500 font-bold uppercase tracking-wider">Active Model:</span>
                                    <span className="text-xs text-blue-400 font-mono">
                                        {activeModelForTask === 'default' ? 'Mistral-Large (Default)' : activeModelForTask}
                                    </span>
                                </div>
                                <div className="ml-2 text-[10px] text-stone-500">
                                    (Managed in <span className="text-stone-400 underline decoration-dotted" title="Go to Models & Config">Models & Config</span>)
                                </div>
                            </div>
                        ) : (
                            <span className="text-stone-500">No task selected</span>
                        )}
                    </div>
                </div>

                {!selectedTask ? (
                    <div className="flex-1 flex items-center justify-center text-stone-500 flex-col gap-2">
                        <LayoutTemplate size={48} className="opacity-20" />
                        <p>Select a task to edit instructions</p>
                    </div>
                ) : (
                    <PromptContextArea
                        key={`${selectedTask}-${selectedModel}`} // Reset on change
                        task={selectedTask}
                        model={selectedModel}
                        initialContent={effectivePromptText}
                        initialDescription={effectiveDescription}
                        initialVariables={effectiveVariables}
                        history={promptHistory}
                        onSave={handleSaveContext}
                        onRollback={handleRollback}
                    />
                )}
            </div>
        </div>
    );
};

/**
 * The Editor Component (Right Side)
 * Manages Tab state (Edit vs History vs Test) and Local Edits
 */
const PromptContextArea = ({ task, model, initialContent, initialDescription, initialVariables, history, onSave, onRollback }) => {
    const [activeTab, setActiveTab] = useState('edit');

    // Editor State
    const [content, setContent] = useState(initialContent || "");
    const [description, setDescription] = useState(initialDescription || "");
    const [variables, setVariables] = useState(initialVariables || []);

    // Test State
    const [testInput, setTestInput] = useState('');
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);

    // Variable Input State
    const [newVar, setNewVar] = useState('');

    const handleAddVar = () => {
        if (newVar && !variables.includes(newVar)) {
            setVariables([...variables, newVar]);
            setNewVar('');
        }
    };

    const removeVar = (v) => {
        setVariables(variables.filter(x => x !== v));
    };

    const handleSave = () => {
        onSave(content, { description, variables });
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await fetch('/api/admin/ai/test', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    model_name: model === 'default' ? null : model,
                    task_type: task,
                    input_data: testInput,
                    prompt_override: content // Test with CURRENT editor content
                })
            });
            const data = await res.json();
            setTestResult(data);
        } catch (err) {
            setTestResult({ error: err.message });
        }
        setTesting(false);
    };

    return (
        <div className="flex-1 flex flex-col h-full overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-stone-800 px-4 bg-stone-950/20">
                <button onClick={() => setActiveTab('edit')} className={`px-4 py-3 text-xs font-bold border-b-2 transition-colors ${activeTab === 'edit' ? 'border-blue-500 text-white' : 'border-transparent text-stone-500 hover:text-stone-300'}`}>
                    Editor & Metadata
                </button>
                <button onClick={() => setActiveTab('test')} className={`px-4 py-3 text-xs font-bold border-b-2 transition-colors ${activeTab === 'test' ? 'border-green-500 text-white' : 'border-transparent text-stone-500 hover:text-stone-300'}`}>
                    Live Simulation
                </button>
                <button onClick={() => setActiveTab('history')} className={`px-4 py-3 text-xs font-bold border-b-2 transition-colors ${activeTab === 'history' ? 'border-purple-500 text-white' : 'border-transparent text-stone-500 hover:text-stone-300'}`}>
                    Version History ({history.length})
                </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-stone-900">
                {/* --- EDIT TAB --- */}
                {activeTab === 'edit' && (
                    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-6 h-full">

                        {/* Metadata Box */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-bold text-stone-500 mb-1 block">Description</label>
                                <input
                                    className="w-full bg-stone-950 border border-stone-800 rounded px-3 py-2 text-sm text-stone-300 focus:border-blue-500 outline-none"
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    placeholder="Describe what this task does..."
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-stone-500 mb-1 block">Variables (Injected Context)</label>
                                <div className="flex items-center gap-2 mb-2">
                                    <input
                                        className="flex-1 bg-stone-950 border border-stone-800 rounded px-2 py-1 text-xs text-stone-300 outline-none"
                                        placeholder="Add var..."
                                        value={newVar}
                                        onChange={e => setNewVar(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleAddVar()}
                                    />
                                    <button onClick={handleAddVar} className="bg-stone-800 p-1 rounded hover:bg-stone-700 text-stone-400"><Plus size={14} /></button>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {variables.map(v => (
                                        <span key={v} className="bg-blue-900/30 text-blue-300 px-2 py-0.5 rounded text-[10px] border border-blue-900/50 flex items-center gap-1">
                                            {v}
                                            <button onClick={() => removeVar(v)} className="hover:text-white">×</button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Editor */}
                        <div className="flex-1 flex flex-col min-h-[400px]">
                            <label className="text-xs font-bold text-stone-500 mb-2 flex justify-between items-center">
                                System Instructions (Prompt)
                                <span className="text-[10px] font-normal text-stone-600">Markdown Supported</span>
                            </label>
                            <textarea
                                className="flex-1 w-full bg-[#1e1e1e] border border-stone-800 rounded p-4 font-mono text-sm text-stone-300 leading-relaxed outline-none focus:border-blue-500 resize-none selection:bg-blue-900/50"
                                value={content}
                                onChange={e => setContent(e.target.value)}
                                spellCheck={false}
                            />
                        </div>

                        <div className="flex justify-end pt-2 border-t border-stone-800">
                            <button onClick={handleSave} className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded font-bold shadow-lg shadow-blue-900/20 flex items-center gap-2">
                                <Save size={18} /> Save New Version
                            </button>
                        </div>
                    </div>
                )}

                {/* --- TEST TAB --- */}
                {activeTab === 'test' && (
                    <div className="p-6 h-full flex flex-col gap-4">
                        <div className="h-1/3 flex flex-col">
                            <label className="text-xs font-bold text-stone-500 mb-2">Simulated User Input / Context</label>
                            <textarea
                                className="flex-1 w-full bg-stone-950 border border-stone-800 rounded p-4 font-mono text-xs text-stone-300 outline-none focus:border-green-500 resize-none"
                                value={testInput}
                                onChange={e => setTestInput(e.target.value)}
                                placeholder="Enter what the user would say..."
                            />
                        </div>
                        <div className="flex justify-end">
                            <button onClick={handleTest} disabled={testing} className="bg-green-600 hover:bg-green-500 text-white px-6 py-2 rounded font-bold flex items-center gap-2">
                                {testing ? <RefreshCw size={16} className="animate-spin" /> : <Play size={16} />}
                                Run Simulation
                            </button>
                        </div>
                        <div className="flex-1 flex flex-col border-t border-stone-800 pt-4">
                            <label className="text-xs font-bold text-stone-500 mb-2">AI Response</label>
                            <div className="flex-1 bg-black/40 border border-stone-800 rounded p-4 overflow-auto font-mono text-xs text-green-400">
                                {testResult ? (
                                    <>
                                        {testResult.error && <div className="text-red-400 mb-2">{testResult.error}</div>}
                                        <div className="prose prose-invert prose-sm max-w-none">
                                            <ReactMarkdown>{testResult.response || ""}</ReactMarkdown>
                                        </div>
                                        {/* Debug info */}
                                        <div className="mt-8 pt-4 border-t border-stone-800/50 text-stone-600">
                                            <div>Model used: {testResult.model_used}</div>
                                            <div>Tool used: {testResult.tool_used || 'None'}</div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-stone-600 italic">Waiting for simulation run...</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* --- HISTORY TAB --- */}
                {activeTab === 'history' && (
                    <div className="p-6">
                        <div className="flex flex-col gap-3">
                            {history.length === 0 && <div className="text-stone-500 italic">No history available.</div>}
                            {history.map((ver, idx) => (
                                <div key={ver.id || idx} className={`border border-stone-800 rounded bg-stone-950/40 p-4 ${ver.is_active ? 'border-green-900/50 bg-green-900/5' : ''}`}>
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="flex items-center gap-3">
                                            <div className="text-sm font-bold text-white">Version {ver.version}</div>
                                            {ver.is_active && <span className="bg-green-900 text-green-300 text-[10px] px-2 py-0.5 rounded font-bold border border-green-700">ACTIVE</span>}
                                            <div className="text-xs text-stone-500">{ver.created_at ? new Date(ver.created_at).toLocaleString() : ''}</div>
                                        </div>
                                        {!ver.is_active && (
                                            <button onClick={() => onRollback(ver)} className="text-xs flex items-center gap-1 text-stone-400 hover:text-white bg-stone-800 px-2 py-1 rounded">
                                                <RotateCcw size={12} /> Rollback
                                            </button>
                                        )}
                                    </div>
                                    <div className="bg-stone-900/50 p-2 rounded text-xs font-mono text-stone-400 max-h-32 overflow-hidden relative">
                                        {ver.prompt_text}
                                        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-stone-900 to-transparent pointer-events-none"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PromptManager;
